import { type Area } from 'react-easy-crop'

/**
 * Crops an image based on the specified cropped area and maintains high quality by working
 * with the original image dimensions and calculating proper scaling.
 *
 * @param imageSrc - The source URL of the image.
 * @param croppedAreaPixels - The area to be cropped in pixel coordinates from the cropper.
 * @param maxResolution - The maximum resolution for the output image.
 * @param contentType - The content type of the output image.
 * @param quality - The quality for lossy formats (0-1).
 * @returns - A promise that resolves to a base64 string of the cropped image.
 */
export const getCroppedImg = (
  imageSrc: string,
  croppedAreaPixels: Area,
  maxResolution: number = 512,
  contentType: string = 'image/webp',
  quality: number = 0.95
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous' // Helps avoid CORS issues
    image.src = imageSrc

    image.onload = () => {
      // The cropped area pixels are based on the displayed image in the cropper
      // We need to scale them to work with the original image dimensions
      const naturalWidth = image.naturalWidth
      const naturalHeight = image.naturalHeight

      // If naturalWidth/naturalHeight are 0, use the displayed dimensions
      const imageWidth = naturalWidth || image.width
      const imageHeight = naturalHeight || image.height

      // Calculate scale factors - this handles cases where the image is displayed
      // at a different size than its natural dimensions in the cropper
      const scaleX = imageWidth / (image.width || 1)
      const scaleY = imageHeight / (image.height || 1)

      // Apply scaling to get the actual pixel coordinates in the original image
      const actualCropArea = {
        x: Math.round(croppedAreaPixels.x * scaleX),
        y: Math.round(croppedAreaPixels.y * scaleY),
        width: Math.round(croppedAreaPixels.width * scaleX),
        height: Math.round(croppedAreaPixels.height * scaleY),
      }

      // Create canvas for the cropped area at original resolution
      const cropCanvas = document.createElement('canvas')
      const cropCtx = cropCanvas.getContext('2d')

      if (!cropCtx) {
        reject(new Error('Canvas context not found'))
        return
      }

      // Set canvas size to the actual crop dimensions
      cropCanvas.width = actualCropArea.width
      cropCanvas.height = actualCropArea.height

      // Draw the cropped portion from the original image
      cropCtx.drawImage(
        image,
        actualCropArea.x,
        actualCropArea.y,
        actualCropArea.width,
        actualCropArea.height, // Source rectangle
        0,
        0,
        actualCropArea.width,
        actualCropArea.height // Destination rectangle
      )

      // Only resize if the cropped area exceeds maxResolution
      let finalWidth = actualCropArea.width
      let finalHeight = actualCropArea.height

      if (finalWidth > maxResolution || finalHeight > maxResolution) {
        const scale = Math.min(
          maxResolution / finalWidth,
          maxResolution / finalHeight
        )
        finalWidth = Math.round(finalWidth * scale)
        finalHeight = Math.round(finalHeight * scale)

        // Create final canvas with resized dimensions
        const finalCanvas = document.createElement('canvas')
        const finalCtx = finalCanvas.getContext('2d')

        if (!finalCtx) {
          reject(new Error('Final canvas context not found'))
          return
        }

        finalCanvas.width = finalWidth
        finalCanvas.height = finalHeight

        // Use high-quality scaling
        finalCtx.imageSmoothingEnabled = true
        finalCtx.imageSmoothingQuality = 'high'
        finalCtx.drawImage(cropCanvas, 0, 0, finalWidth, finalHeight)

        // Convert to base64 with quality control
        resolve(finalCanvas.toDataURL(contentType, quality))
      } else {
        // No resizing needed, use the crop canvas directly
        resolve(cropCanvas.toDataURL(contentType, quality))
      }
    }

    image.onerror = (error: unknown) => reject(error)
  })
}

/**
 * Converts a base64 data URL to a File object.
 *
 * @param dataUrl - The base64 data URL.
 * @param fileName - The name for the output file.
 * @returns - A promise that resolves to a File object.
 */
export const dataURLtoFile = async (
  dataUrl: string,
  fileName: string
): Promise<File> => {
  const res = await fetch(dataUrl)
  const blob = await res.blob()
  return new File([blob], fileName, { type: blob.type })
}
